/**
 * inventoryService.js — Inventory Management Service (browser/web only)
 * Mirrors supabase.js service pattern. All methods restaurant-scoped.
 */
import { supabase } from './supabase';

// ─── Categories ──────────────────────────────────────────────────────────────

export const invCategoryService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_categories')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('display_order', { ascending: true })
      .order('name', { ascending: true });
    return { data, error };
  },

  async upsert(restaurantId, category) {
    const payload = {
      restaurant_id: restaurantId,
      name: category.name.trim(),
      display_order: category.display_order ?? 0,
      is_active: category.is_active ?? true,
      updated_at: new Date().toISOString(),
    };
    if (category.id) payload.id = category.id;
    const { data, error } = await supabase
      .from('inventory_categories')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    return { data, error };
  },

  async setActive(id, isActive) {
    const { error } = await supabase
      .from('inventory_categories')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { error };
  },
};

// ─── Units ───────────────────────────────────────────────────────────────────

export const invUnitService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_units')
      .select('*')
      .or(`restaurant_id.is.null,restaurant_id.eq.${restaurantId}`)
      .order('name', { ascending: true });
    return { data, error };
  },

  async create(restaurantId, unit) {
    const { data, error } = await supabase
      .from('inventory_units')
      .insert({
        restaurant_id: restaurantId,
        name: unit.name.trim(),
        short_code: unit.short_code.trim(),
        unit_type: unit.unit_type || 'count',
        allow_decimal: unit.allow_decimal ?? true,
      })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Suppliers ───────────────────────────────────────────────────────────────

export const invSupplierService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_suppliers')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('name', { ascending: true });
    return { data, error };
  },

  async upsert(restaurantId, supplier) {
    const payload = {
      restaurant_id: restaurantId,
      name: supplier.name.trim(),
      phone: supplier.phone || null,
      email: supplier.email || null,
      gstin: supplier.gstin || null,
      address: supplier.address || null,
      contact_person: supplier.contact_person || null,
      notes: supplier.notes || null,
      is_active: supplier.is_active ?? true,
      updated_at: new Date().toISOString(),
    };
    if (supplier.id) payload.id = supplier.id;
    const { data, error } = await supabase
      .from('inventory_suppliers')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    return { data, error };
  },

  async setActive(id, isActive) {
    const { error } = await supabase
      .from('inventory_suppliers')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { error };
  },
};

// ─── Inventory Items ─────────────────────────────────────────────────────────

export const invItemService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_items')
      .select(`
        *,
        category:inventory_categories(id, name),
        base_unit:inventory_units!inventory_items_base_unit_id_fkey(id, name, short_code),
        purchase_unit:inventory_units!inventory_items_purchase_unit_id_fkey(id, name, short_code),
        supplier:inventory_suppliers(id, name)
      `)
      .eq('restaurant_id', restaurantId)
      .order('name', { ascending: true });
    return { data, error };
  },

  async upsert(restaurantId, item) {
    const payload = {
      restaurant_id:          restaurantId,
      name:                   item.name.trim(),
      item_code:              item.item_code || null,
      item_type:              item.item_type || 'raw_material',
      category_id:            item.category_id || null,
      base_unit_id:           item.base_unit_id || null,
      purchase_unit_id:       item.purchase_unit_id || null,
      conversion_factor:      parseFloat(item.conversion_factor) || 1,
      min_stock_threshold:    parseFloat(item.min_stock_threshold) || 0,
      ideal_stock_threshold:  item.ideal_stock_threshold ? parseFloat(item.ideal_stock_threshold) : null,
      preferred_supplier_id:  item.preferred_supplier_id || null,
      is_active:              item.is_active ?? true,
      notes:                  item.notes || null,
      updated_at:             new Date().toISOString(),
    };
    if (item.id) payload.id = item.id;
    const { data, error } = await supabase
      .from('inventory_items')
      .upsert(payload, { onConflict: 'id' })
      .select()
      .single();
    return { data, error };
  },

  async setActive(id, isActive) {
    const { error } = await supabase
      .from('inventory_items')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { error };
  },

  // Opening stock setup — only call before first transactions
  async setOpeningStock(restaurantId, itemId, qty, value, createdBy) {
    // Update item
    const { error: itemErr } = await supabase
      .from('inventory_items')
      .update({
        current_stock:        qty,
        opening_stock:        qty,
        opening_stock_value:  value,
        avg_unit_cost:        qty > 0 ? (value / qty) : 0,
        updated_at:           new Date().toISOString(),
      })
      .eq('id', itemId);
    if (itemErr) return { error: itemErr };

    // Ledger entry
    const { error: movErr } = await supabase
      .from('stock_movements')
      .insert({
        restaurant_id:      restaurantId,
        inventory_item_id:  itemId,
        movement_type:      'opening_stock',
        reference_type:     'adjustment',
        quantity_change:    qty,
        unit_cost:          qty > 0 ? (value / qty) : 0,
        stock_before:       0,
        stock_after:        qty,
        remarks:            'Opening stock entry',
        created_by:         createdBy || null,
      });
    return { error: movErr };
  },
};

// ─── Recipes ─────────────────────────────────────────────────────────────────

export const invRecipeService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_recipes')
      .select(`
        *,
        menu_item:menu_items(id, name, price),
        items:inventory_recipe_items(
          id, quantity, wastage_percent,
          inventory_item:inventory_items(id, name, avg_unit_cost),
          unit:inventory_units(id, name, short_code)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async upsert(restaurantId, recipe, ingredients) {
    // Upsert recipe header
    const headerPayload = {
      restaurant_id:  restaurantId,
      menu_item_id:   recipe.menu_item_id,
      is_active:      recipe.is_active ?? true,
      yield_qty:      parseFloat(recipe.yield_qty) || 1,
      notes:          recipe.notes || null,
      updated_at:     new Date().toISOString(),
    };
    if (recipe.id) headerPayload.id = recipe.id;

    const { data: recipeRow, error: recipeErr } = await supabase
      .from('inventory_recipes')
      .upsert(headerPayload, { onConflict: 'restaurant_id,menu_item_id' })
      .select()
      .single();
    if (recipeErr) return { error: recipeErr };

    // Replace all ingredients
    await supabase.from('inventory_recipe_items').delete().eq('recipe_id', recipeRow.id);
    if (ingredients && ingredients.length > 0) {
      const rows = ingredients.map(ing => ({
        recipe_id:          recipeRow.id,
        inventory_item_id:  ing.inventory_item_id,
        quantity:           parseFloat(ing.quantity) || 0,
        unit_id:            ing.unit_id || null,
        wastage_percent:    parseFloat(ing.wastage_percent) || 0,
      }));
      const { error: ingErr } = await supabase.from('inventory_recipe_items').insert(rows);
      if (ingErr) return { error: ingErr };
    }
    return { data: recipeRow, error: null };
  },

  async toggleActive(id, isActive) {
    const { error } = await supabase
      .from('inventory_recipes')
      .update({ is_active: isActive, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { error };
  },
};

// ─── Purchase Orders ──────────────────────────────────────────────────────────

export const invPOService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('purchase_orders')
      .select(`
        *,
        supplier:inventory_suppliers(id, name, phone),
        items:purchase_order_items(
          id, inventory_item_id, purchase_unit_id, ordered_qty, received_qty, unit_price, line_total,
          inventory_item:inventory_items(id, name),
          purchase_unit:inventory_units(id, name, short_code)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async create(restaurantId, po, lineItems, createdBy) {
    // Claim PO number
    const { data: poNum, error: numErr } = await supabase.rpc('claim_next_inventory_number', {
      p_restaurant_id: restaurantId,
      p_prefix: 'PO',
    });
    if (numErr) return { error: numErr };

    const subtotal = lineItems.reduce((s, l) => s + (parseFloat(l.line_total) || 0), 0);
    const { data: poRow, error: poErr } = await supabase
      .from('purchase_orders')
      .insert({
        restaurant_id:          restaurantId,
        po_number:              poNum,
        supplier_id:            po.supplier_id || null,
        order_date:             po.order_date || new Date().toISOString().slice(0, 10),
        expected_delivery_date: po.expected_delivery_date || null,
        status:                 'draft',
        notes:                  po.notes || null,
        subtotal,
        tax_amount:             parseFloat(po.tax_amount) || 0,
        total_amount:           subtotal + (parseFloat(po.tax_amount) || 0),
        created_by:             createdBy || null,
      })
      .select()
      .single();
    if (poErr) return { error: poErr };

    const rows = lineItems.map(l => ({
      po_id:              poRow.id,
      inventory_item_id:  l.inventory_item_id,
      ordered_qty:        parseFloat(l.ordered_qty) || 0,
      purchase_unit_id:   l.purchase_unit_id || null,
      unit_price:         parseFloat(l.unit_price) || 0,
      line_total:         parseFloat(l.line_total) || 0,
    }));
    const { error: itemErr } = await supabase.from('purchase_order_items').insert(rows);
    return { data: poRow, error: itemErr };
  },

  async updateStatus(id, status) {
    const { error } = await supabase
      .from('purchase_orders')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id);
    return { error };
  },
};

// ─── Stock Inward / GRN ───────────────────────────────────────────────────────

export const invInwardService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('stock_inward')
      .select(`
        *,
        supplier:inventory_suppliers(id, name),
        po:purchase_orders(id, po_number),
        items:stock_inward_items(
          id, received_qty, base_qty, purchase_price, line_value,
          inventory_item:inventory_items(id, name),
          purchase_unit:inventory_units(id, name, short_code)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async create(restaurantId, grn, lineItems, createdBy) {
    const { data: grnNum, error: numErr } = await supabase.rpc('claim_next_inventory_number', {
      p_restaurant_id: restaurantId,
      p_prefix: 'GRN',
    });
    if (numErr) return { error: numErr };

    const totalValue = lineItems.reduce((s, l) => s + (parseFloat(l.line_value) || 0), 0);
    const { data: grnRow, error: grnErr } = await supabase
      .from('stock_inward')
      .insert({
        restaurant_id:  restaurantId,
        grn_number:     grnNum,
        source_type:    grn.po_id ? 'po' : 'manual',
        po_id:          grn.po_id || null,
        supplier_id:    grn.supplier_id || null,
        inward_date:    grn.inward_date || new Date().toISOString().slice(0, 10),
        invoice_ref:    grn.invoice_ref || null,
        notes:          grn.notes || null,
        total_value:    totalValue,
        created_by:     createdBy || null,
      })
      .select()
      .single();
    if (grnErr) return { error: grnErr };

    // Insert line items + update stock (weighted average cost)
    for (const line of lineItems) {
      const qty = parseFloat(line.base_qty) || 0;
      const price = parseFloat(line.purchase_price) || 0;
      if (qty <= 0) continue;

      // Insert inward line
      await supabase.from('stock_inward_items').insert({
        inward_id:          grnRow.id,
        inventory_item_id:  line.inventory_item_id,
        received_qty:       parseFloat(line.received_qty) || qty,
        purchase_unit_id:   line.purchase_unit_id || null,
        base_qty:           qty,
        purchase_price:     price,
        line_value:         parseFloat(line.line_value) || qty * price,
      });

      // Fetch current stock + cost for weighted avg
      const { data: item } = await supabase
        .from('inventory_items')
        .select('current_stock, avg_unit_cost')
        .eq('id', line.inventory_item_id)
        .single();

      const existingStock = parseFloat(item?.current_stock) || 0;
      const existingCost  = parseFloat(item?.avg_unit_cost)  || 0;
      const newTotal      = existingStock + qty;
      const newAvgCost    = newTotal > 0
        ? ((existingStock * existingCost) + (qty * price)) / newTotal
        : price;

      // Update stock
      await supabase.from('inventory_items').update({
        current_stock:        newTotal,
        avg_unit_cost:        newAvgCost,
        latest_purchase_price: price,
        updated_at:            new Date().toISOString(),
      }).eq('id', line.inventory_item_id);

      // Ledger
      await supabase.from('stock_movements').insert({
        restaurant_id:      restaurantId,
        inventory_item_id:  line.inventory_item_id,
        movement_type:      'purchase_inward',
        reference_type:     'inward',
        reference_id:       grnRow.id,
        quantity_change:    qty,
        unit_cost:          price,
        stock_before:       existingStock,
        stock_after:        newTotal,
        remarks:            `GRN: ${grnNum}`,
        created_by:         createdBy || null,
      });
    }

    // If from PO, update PO received qty + status (best-effort — don't fail the GRN if this errors)
    if (grn.po_id) {
      try {
        const { data: poItemRows } = await supabase
          .from('purchase_order_items')
          .select('id, inventory_item_id, ordered_qty, received_qty')
          .eq('po_id', grn.po_id);

        for (const line of lineItems) {
          const poItem = (poItemRows || []).find(r => r.inventory_item_id === line.inventory_item_id);
          if (poItem) {
            const newQty = (parseFloat(poItem.received_qty) || 0) + (parseFloat(line.received_qty) || 0);
            await supabase.from('purchase_order_items')
              .update({ received_qty: newQty })
              .eq('id', poItem.id);
          }
        }

        const { data: updatedItems } = await supabase
          .from('purchase_order_items')
          .select('ordered_qty, received_qty')
          .eq('po_id', grn.po_id);
        const allReceived = (updatedItems || []).every(i => (parseFloat(i.received_qty) || 0) >= (parseFloat(i.ordered_qty) || 0));
        await supabase.from('purchase_orders')
          .update({ status: allReceived ? 'received' : 'part_received', updated_at: new Date().toISOString() })
          .eq('id', grn.po_id);
      } catch (_) {
        // PO status update failed — GRN is still saved
      }
    }

    return { data: grnRow, error: null };
  },
};

// ─── Stock Adjustments ───────────────────────────────────────────────────────

export const invAdjustmentService = {
  async getAll(restaurantId, limit = 100) {
    const { data, error } = await supabase
      .from('stock_adjustments')
      .select(`
        *,
        inventory_item:inventory_items(id, name),
        unit:inventory_units(id, name, short_code)
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data, error };
  },

  async create(restaurantId, adj, createdBy) {
    const qty = parseFloat(adj.quantity) || 0;
    const isIncrease = adj.adjustment_type === 'increase';

    const { data: item } = await supabase
      .from('inventory_items')
      .select('current_stock, avg_unit_cost')
      .eq('id', adj.inventory_item_id)
      .single();

    const stockBefore = parseFloat(item?.current_stock) || 0;
    const stockAfter  = isIncrease ? stockBefore + qty : Math.max(stockBefore - qty, 0);

    const { error: adjErr } = await supabase.from('stock_adjustments').insert({
      restaurant_id:      restaurantId,
      inventory_item_id:  adj.inventory_item_id,
      adjustment_type:    adj.adjustment_type,
      reason:             adj.reason,
      quantity:           qty,
      unit_id:            adj.unit_id || null,
      notes:              adj.notes || null,
      stock_before:       stockBefore,
      stock_after:        stockAfter,
      created_by:         createdBy || null,
    });
    if (adjErr) return { error: adjErr };

    await supabase.from('inventory_items').update({
      current_stock: stockAfter,
      updated_at:    new Date().toISOString(),
    }).eq('id', adj.inventory_item_id);

    await supabase.from('stock_movements').insert({
      restaurant_id:      restaurantId,
      inventory_item_id:  adj.inventory_item_id,
      movement_type:      isIncrease ? 'manual_adjustment_increase' : 'manual_adjustment_decrease',
      reference_type:     'adjustment',
      quantity_change:    isIncrease ? qty : -qty,
      unit_cost:          item?.avg_unit_cost || 0,
      stock_before:       stockBefore,
      stock_after:        stockAfter,
      remarks:            `${adj.reason}: ${adj.notes || ''}`.trim(),
      created_by:         createdBy || null,
    });

    return { error: null };
  },
};

// ─── Wastage ─────────────────────────────────────────────────────────────────

export const invWastageService = {
  async getAll(restaurantId, limit = 100) {
    const { data, error } = await supabase
      .from('wastage_entries')
      .select(`
        *,
        inventory_item:inventory_items(id, name, avg_unit_cost),
        unit:inventory_units(id, name, short_code)
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })
      .limit(limit);
    return { data, error };
  },

  async create(restaurantId, entry, createdBy) {
    const qty = parseFloat(entry.quantity) || 0;

    const { data: item } = await supabase
      .from('inventory_items')
      .select('current_stock, avg_unit_cost')
      .eq('id', entry.inventory_item_id)
      .single();

    const stockBefore = parseFloat(item?.current_stock) || 0;
    const stockAfter  = Math.max(stockBefore - qty, 0);

    const { error: wErr } = await supabase.from('wastage_entries').insert({
      restaurant_id:      restaurantId,
      inventory_item_id:  entry.inventory_item_id,
      quantity:           qty,
      unit_id:            entry.unit_id || null,
      reason:             entry.reason,
      notes:              entry.notes || null,
      wastage_date:       entry.wastage_date || new Date().toISOString().slice(0, 10),
      created_by:         createdBy || null,
    });
    if (wErr) return { error: wErr };

    await supabase.from('inventory_items').update({
      current_stock: stockAfter,
      updated_at:    new Date().toISOString(),
    }).eq('id', entry.inventory_item_id);

    await supabase.from('stock_movements').insert({
      restaurant_id:      restaurantId,
      inventory_item_id:  entry.inventory_item_id,
      movement_type:      'wastage',
      reference_type:     'wastage',
      quantity_change:    -qty,
      unit_cost:          item?.avg_unit_cost || 0,
      stock_before:       stockBefore,
      stock_after:        stockAfter,
      remarks:            `Wastage: ${entry.reason}`,
      created_by:         createdBy || null,
    });

    return { error: null };
  },
};

// ─── Stock Audits ────────────────────────────────────────────────────────────

export const invAuditService = {
  async getAll(restaurantId) {
    const { data, error } = await supabase
      .from('stock_audits')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async getWithItems(auditId) {
    const { data, error } = await supabase
      .from('stock_audits')
      .select(`
        *,
        items:stock_audit_items(
          id, system_stock, physical_stock, variance_value, reason,
          inventory_item:inventory_items(id, name, avg_unit_cost,
            base_unit:inventory_units!inventory_items_base_unit_id_fkey(id, name, short_code))
        )
      `)
      .eq('id', auditId)
      .single();
    return { data, error };
  },

  async create(restaurantId, name, createdBy) {
    // Snapshot current stock of all active items
    const { data: items } = await supabase
      .from('inventory_items')
      .select('id, current_stock')
      .eq('restaurant_id', restaurantId)
      .eq('is_active', true);

    const { data: auditRow, error: auditErr } = await supabase
      .from('stock_audits')
      .insert({
        restaurant_id:  restaurantId,
        name:           name.trim(),
        audit_date:     new Date().toISOString().slice(0, 10),
        status:         'in_progress',
        created_by:     createdBy || null,
      })
      .select()
      .single();
    if (auditErr) return { error: auditErr };

    // Create audit item rows with system stock snapshot
    const auditItems = (items || []).map(item => ({
      audit_id:           auditRow.id,
      inventory_item_id:  item.id,
      system_stock:       item.current_stock,
      physical_stock:     null,
    }));
    if (auditItems.length > 0) {
      await supabase.from('stock_audit_items').insert(auditItems);
    }

    return { data: auditRow, error: null };
  },

  async updateAuditItem(auditItemId, physicalStock, reason) {
    const { error } = await supabase
      .from('stock_audit_items')
      .update({ physical_stock: physicalStock, reason: reason || null })
      .eq('id', auditItemId);
    return { error };
  },

  async complete(restaurantId, auditId, createdBy) {
    // Get audit items
    const { data: auditItems } = await supabase
      .from('stock_audit_items')
      .select('*, inventory_item:inventory_items(current_stock, avg_unit_cost)')
      .eq('audit_id', auditId)
      .not('physical_stock', 'is', null);

    // Post variance adjustments
    for (const ai of (auditItems || [])) {
      const variance = (ai.physical_stock || 0) - ai.system_stock;
      if (Math.abs(variance) < 0.0001) continue;

      const stockBefore = parseFloat(ai.inventory_item?.current_stock) || 0;
      const stockAfter  = stockBefore + variance;
      const varValue    = Math.abs(variance) * (ai.inventory_item?.avg_unit_cost || 0);

      await supabase.from('stock_audit_items').update({
        variance_value: varValue,
      }).eq('id', ai.id);

      await supabase.from('inventory_items').update({
        current_stock: Math.max(stockAfter, 0),
        updated_at:    new Date().toISOString(),
      }).eq('id', ai.inventory_item_id);

      await supabase.from('stock_movements').insert({
        restaurant_id:      restaurantId,
        inventory_item_id:  ai.inventory_item_id,
        movement_type:      'stock_audit_variance',
        reference_type:     'audit',
        reference_id:       auditId,
        quantity_change:    variance,
        unit_cost:          ai.inventory_item?.avg_unit_cost || 0,
        stock_before:       stockBefore,
        stock_after:        Math.max(stockAfter, 0),
        remarks:            ai.reason || 'Physical count variance',
        created_by:         createdBy || null,
      });
    }

    // Mark audit complete
    const { error } = await supabase
      .from('stock_audits')
      .update({ status: 'completed', completed_at: new Date().toISOString() })
      .eq('id', auditId);
    return { error };
  },
};

// ─── Stock Movements ─────────────────────────────────────────────────────────

export const invMovementService = {
  async getAll(restaurantId, { startDate, endDate, itemId } = {}) {
    let q = supabase
      .from('stock_movements')
      .select(`
        *,
        inventory_item:inventory_items(id, name)
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })
      .limit(500);
    if (startDate) q = q.gte('created_at', startDate.toISOString());
    if (endDate)   q = q.lte('created_at', endDate.toISOString());
    if (itemId)    q = q.eq('inventory_item_id', itemId);
    const { data, error } = await q;
    return { data, error };
  },
};

// ─── Inventory Settings ──────────────────────────────────────────────────────

export const invSettingsService = {
  async get(restaurantId) {
    const { data, error } = await supabase
      .from('inventory_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .maybeSingle();
    return { data, error };
  },

  async save(restaurantId, settings) {
    const { data, error } = await supabase
      .from('inventory_settings')
      .upsert({
        restaurant_id:              restaurantId,
        allow_negative_stock:       !!settings.allow_negative_stock,
        auto_deduct_on_billing:     settings.auto_deduct_on_billing ?? true,
        low_stock_alerts_enabled:   settings.low_stock_alerts_enabled ?? true,
        menu_availability_signal:   !!settings.menu_availability_signal,
        costing_method:             settings.costing_method || 'weighted_average',
        qty_decimal_places:         settings.qty_decimal_places ?? 3,
        cost_decimal_places:        settings.cost_decimal_places ?? 2,
        require_supplier_in_po:     !!settings.require_supplier_in_po,
        updated_at:                 new Date().toISOString(),
      }, { onConflict: 'restaurant_id' })
      .select()
      .single();
    return { data, error };
  },
};

// ─── Billing Integration ─────────────────────────────────────────────────────

export const invBillingIntegration = {
  // Called after posService.billOrder() succeeds
  async deductForOrder(restaurantId, orderId) {
    const { data, error } = await supabase.rpc('deduct_stock_for_order', {
      p_order_id:      orderId,
      p_restaurant_id: restaurantId,
    });
    return { data, error };
  },

  // Called when posService.markOrderInvalid() is called
  async reverseForOrder(restaurantId, orderId) {
    const { data, error } = await supabase.rpc('reverse_stock_for_order', {
      p_order_id:      orderId,
      p_restaurant_id: restaurantId,
    });
    return { data, error };
  },
};

// ─── Dashboard Summary ───────────────────────────────────────────────────────

export const invDashboardService = {
  async getSummary(restaurantId) {
    const [itemsRes, movementsRes, poRes, wastageRes] = await Promise.all([
      supabase
        .from('inventory_items')
        .select('id, name, current_stock, min_stock_threshold, avg_unit_cost, is_active')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true),
      supabase
        .from('stock_movements')
        .select('inventory_item_id, quantity_change, movement_type, created_at, inventory_item:inventory_items(name)')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(10),
      supabase
        .from('purchase_orders')
        .select('id, po_number, status, total_amount, created_at')
        .eq('restaurant_id', restaurantId)
        .in('status', ['draft', 'sent'])
        .limit(5),
      supabase
        .from('wastage_entries')
        .select('quantity, inventory_item:inventory_items(avg_unit_cost)')
        .eq('restaurant_id', restaurantId)
        .gte('created_at', new Date(Date.now() - 7 * 86400000).toISOString()),
    ]);

    const items = itemsRes.data || [];
    const totalItems    = items.length;
    const totalValue    = items.reduce((s, i) => s + (i.current_stock * (i.avg_unit_cost || 0)), 0);
    const lowStockItems = items.filter(i => i.current_stock > 0 && i.current_stock <= (i.min_stock_threshold || 0));
    const outOfStock    = items.filter(i => i.current_stock <= 0);

    return {
      data: {
        totalItems,
        totalValue,
        lowStockCount:    lowStockItems.length,
        outOfStockCount:  outOfStock.length,
        lowStockItems:    lowStockItems.slice(0, 5),
        outOfStockItems:  outOfStock.slice(0, 5),
        recentMovements:  movementsRes.data || [],
        pendingPOs:       poRes.data || [],
        weeklyWastageValue: (wastageRes.data || []).reduce(
          (s, w) => s + ((w.quantity || 0) * (w.inventory_item?.avg_unit_cost || 0)), 0
        ),
      },
      error: null,
    };
  },
};
